import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useMatch } from "react-router-dom";
import {
    Activity,
    ChevronDown,
    Clock,
    FileUp,
    FolderPlus,
    FolderUp,
    HardDrive,
    LayoutTemplate,
    Menu as MenuIcon,
    Plus,
    Search,
    Star,
    Trash2,
    Users,
    X
} from "@fg/icons";
import { useAuth } from "react-oidc-context";
import { AppFooter, PlanNotices, SuiteAppBar, SuiteAppTile, SuiteBarButton, suiteApp } from "@fg/ui-theme";
import { useAppearanceSync } from "@fg/ui-theme";
import { useQuota } from "@/api/hooks";
import { Menu, anchorFrom, type MenuAnchor } from "@/components/ui/menu";
import { useFocusTrap } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { formatBytes } from "@/lib/format";
import { ToastProvider } from "@/features/drive/toast/ToastProvider";
import { DialogsProvider } from "@/features/drive/dialogs/DialogsProvider";
import { DriveUiProvider } from "@/features/drive/DriveUiProvider";
import { useDriveActions } from "@/features/drive/driveActions";
import { UploadProvider, useUploads } from "@/features/drive/upload/UploadProvider";
import { SearchBox } from "./SearchBox";

export function DriveShell() {
    return (
        <ToastProvider>
            <DialogsProvider>
                <DriveUiProvider>
                    <UploadProvider>
                        <ShellLayout />
                    </UploadProvider>
                </DriveUiProvider>
            </DialogsProvider>
        </ToastProvider>
    );
}

/** Folder the user is looking at (null on My Drive root and non-folder pages). */
function useCurrentFolderId(): string | null {
    const match = useMatch("/folder/:folderId");
    return match?.params.folderId ?? null;
}

function ShellLayout() {
    const [navOpen, setNavOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const auth = useAuth();
    const location = useLocation();
    const actions = useDriveActions();
    const folderId = useCurrentFolderId();
    const folderRef = useRef(folderId);
    folderRef.current = folderId;

    useEffect(() => {
        setNavOpen(false);
        setSearchOpen(false);
    }, [location.pathname, location.search]);

    // App-wide shortcuts: ? help, / search, N new folder.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
            const t = e.target as HTMLElement | null;
            const editing = Boolean(t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable));
            if (editing || document.querySelector("[data-drive-overlay]")) return;
            if (e.key === "?") {
                e.preventDefault();
                actions.showShortcuts();
            } else if (e.key === "/") {
                e.preventDefault();
                if (window.matchMedia("(min-width: 768px)").matches) {
                    const input = document.querySelector<HTMLInputElement>('input[aria-label="Search Drive"]');
                    input?.focus();
                    input?.select();
                } else {
                    setSearchOpen(true);
                }
            } else if (e.key === "n" || e.key === "N") {
                e.preventDefault();
                void actions.newFolder(folderRef.current);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [actions]);

    // A file dropped outside any drop zone must not navigate the tab away.
    useEffect(() => {
        const prevent = (e: DragEvent) => {
            if (e.dataTransfer?.types.includes("Files")) e.preventDefault();
        };
        window.addEventListener("dragover", prevent);
        window.addEventListener("drop", prevent);
        return () => {
            window.removeEventListener("dragover", prevent);
            window.removeEventListener("drop", prevent);
        };
    }, []);

    // Keep the suite look in step with the account copy (GET/PUT /api/me/appearance on this BFF).
    useAppearanceSync(import.meta.env.VITE_DRIVE_BFF_BASE_URL, auth.user?.access_token);

    const profile = auth.user?.profile;
    const email = profile?.email ?? undefined;
    const user = { name: profile?.name ?? profile?.preferred_username ?? email ?? "Signed in", email };

    return (
        <div className="flex h-[100dvh] flex-col bg-background text-foreground">
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-1 focus:text-sm focus:text-primary-foreground"
            >
                Skip to main content
            </a>
            <SuiteAppBar
                app="drive"
                user={user}
                onSignOut={() => void auth.signoutRedirect()}
                renderBrand={(b) => (
                    <Link to="/" className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current">
                        {b}
                    </Link>
                )}
                leading={
                    <SuiteBarButton label="Open navigation" aria-expanded={navOpen} onClick={() => setNavOpen(true)} className="lg:hidden">
                        <MenuIcon className="h-5 w-5" aria-hidden />
                    </SuiteBarButton>
                }
                search={<SearchBox mobileOpen={searchOpen} onMobileClose={() => setSearchOpen(false)} />}
                actions={
                    <SuiteBarButton label="Open search" onClick={() => setSearchOpen(true)} className="md:hidden">
                        <Search className="h-5 w-5" aria-hidden />
                    </SuiteBarButton>
                }
                onHelp={actions.showShortcuts}
                onShortcuts={actions.showShortcuts}
            />
            {/* FPT-23: domain setup (admins) and billing notices. */}
            <PlanNotices baseUrl={import.meta.env.VITE_DRIVE_BFF_BASE_URL} token={auth.user?.access_token} />
            <div className="flex min-h-0 flex-1">
                <DriveSidebar className="hidden lg:flex" />
                {navOpen ? <MobileDrawer onClose={() => setNavOpen(false)} /> : null}
                <main id="main-content" tabIndex={-1} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden outline-none">
                    <Outlet />
                </main>
            </div>
            <AppFooter />
        </div>
    );
}

function MobileDrawer({ onClose }: { onClose: () => void }) {
    const panelRef = useRef<HTMLDivElement>(null);
    useFocusTrap(panelRef);
    return (
        <div
            data-drive-overlay=""
            className="fixed inset-0 z-50 lg:hidden"
            onKeyDown={(e) => {
                if (e.key === "Escape") {
                    e.stopPropagation();
                    onClose();
                }
            }}
        >
            <div className="drive-fade-in absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label="Navigation"
                tabIndex={-1}
                className="drive-slide-right absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-[var(--drive-pane)] shadow-2xl outline-none"
            >
                <div className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--drive-line)] pl-4 pr-2">
                    <SuiteAppTile app="drive" size={24} />
                    <span className="flex-1 font-semibold">Drive</span>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close navigation"
                        className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <DriveSidebar className="flex min-h-0 w-full flex-1 border-r-0" onNavigate={onClose} />
            </div>
        </div>
    );
}

/** Left pane: split New, My Drive / Shared / Recent / Starred / Activity / Bin, storage meter. */
function DriveSidebar({ className, onNavigate }: { className?: string; onNavigate?: () => void }) {
    const location = useLocation();
    const links = [
        { to: "/", label: "My Drive", icon: HardDrive, active: location.pathname === "/" || location.pathname.startsWith("/folder/") },
        { to: "/shared-with-me", label: "Shared with me", icon: Users, active: location.pathname === "/shared-with-me" },
        { to: "/recent", label: "Recent", icon: Clock, active: location.pathname === "/recent" },
        { to: "/favorites", label: "Starred", icon: Star, active: location.pathname === "/favorites" || location.pathname === "/starred" },
        { to: "/activity", label: "Activity", icon: Activity, active: location.pathname === "/activity" },
        { to: "/trash", label: "Bin", icon: Trash2, active: location.pathname === "/trash" }
    ];
    return (
        <nav
            aria-label="Drive"
            className={cn("w-[248px] shrink-0 flex-col overflow-y-auto border-r border-[var(--drive-line)] bg-[var(--drive-pane)] px-2 py-3", className)}
        >
            <NewButton onPicked={onNavigate} />
            <ul className="space-y-0.5">
                {links.map((l) => (
                    <li key={l.to}>
                        <NavLink
                            to={l.to}
                            onClick={onNavigate}
                            aria-current={l.active ? "page" : undefined}
                            className={cn(
                                "flex h-8 items-center gap-2.5 rounded-[4px] pl-2.5 pr-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                l.active ? "bg-[var(--drive-selected)] font-semibold text-foreground" : "text-foreground hover:bg-[var(--drive-hover)]"
                            )}
                        >
                            <l.icon className={cn("h-4 w-4 shrink-0", l.active ? "text-primary" : "text-[var(--drive-text-2)]", l.active && l.label === "Starred" && "fill-current")} aria-hidden />
                            {l.label}
                        </NavLink>
                    </li>
                ))}
            </ul>
            <div className="mt-auto">
                <StorageWidget active={location.pathname === "/storage"} onNavigate={onNavigate} />
            </div>
        </nav>
    );
}

/**
 * Split "New": the main part opens the template gallery; the arrow keeps
 * the quick menu — folder, uploads, and blank Document / Spreadsheet /
 * Presentation created in the current folder and opened straight away.
 */
function NewButton({ onPicked }: { onPicked?: () => void }) {
    const actions = useDriveActions();
    const uploads = useUploads();
    const folderId = useCurrentFolderId();
    const [menu, setMenu] = useState<{ anchor: MenuAnchor; keyboard: boolean } | null>(null);
    const wrapRef = useRef<HTMLDivElement>(null);

    const pick = (fn: () => void) => () => {
        onPicked?.();
        fn();
    };
    const tile = (id: "docs" | "sheets" | "slides") => ({ icon: suiteApp(id).icon, iconClass: iconTone[id] });
    const part =
        "flex h-9 items-center bg-primary text-primary-foreground transition-colors hover:bg-[var(--drive-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-foreground";

    return (
        <>
            <div ref={wrapRef} className="mx-1 mb-3 flex shrink-0 overflow-hidden rounded-[4px] shadow-[var(--drive-shadow-4)]">
                <button
                    type="button"
                    aria-haspopup="dialog"
                    onClick={pick(() => actions.openTemplates(folderId))}
                    className={cn(part, "flex-1 gap-2 px-3.5 text-sm font-semibold")}
                >
                    <Plus className="h-4 w-4" aria-hidden />
                    New
                </button>
                <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={Boolean(menu)}
                    aria-label="More ways to create or upload"
                    title="New folder, uploads and blank files"
                    onClick={(e) => wrapRef.current && setMenu({ anchor: anchorFrom(wrapRef.current), keyboard: e.detail === 0 })}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowDown" && wrapRef.current) {
                            e.preventDefault();
                            setMenu({ anchor: anchorFrom(wrapRef.current), keyboard: true });
                        }
                    }}
                    className={cn(part, "border-l border-primary-foreground/35 px-2.5")}
                >
                    <ChevronDown className="h-4 w-4" aria-hidden />
                </button>
            </div>
            {menu ? (
                <Menu
                    label="New"
                    anchor={menu.anchor}
                    focusFirst={menu.keyboard}
                    onClose={() => setMenu(null)}
                    className="w-64"
                    entries={[
                        { id: "folder", label: "New folder", icon: FolderPlus, iconClass: "text-amber-500", shortcut: "N", onSelect: pick(() => void actions.newFolder(folderId)) },
                        { kind: "separator", id: "s1" },
                        { id: "upload-files", label: "File upload", icon: FileUp, onSelect: pick(() => uploads.pickFiles(folderId)) },
                        { id: "upload-folder", label: "Folder upload", icon: FolderUp, onSelect: pick(() => uploads.pickFolder(folderId)) },
                        { kind: "separator", id: "s2" },
                        { id: "doc", label: "Document", ...tile("docs"), onSelect: pick(() => void actions.createFromTemplate("blank-document", folderId)) },
                        { id: "sheet", label: "Spreadsheet", ...tile("sheets"), onSelect: pick(() => void actions.createFromTemplate("blank-workbook", folderId)) },
                        { id: "slides", label: "Presentation", ...tile("slides"), onSelect: pick(() => void actions.createFromTemplate("blank-presentation", folderId)) },
                        { kind: "separator", id: "s3" },
                        { id: "templates", label: "From a template…", icon: LayoutTemplate, onSelect: pick(() => actions.openTemplates(folderId)) }
                    ]}
                />
            ) : null}
        </>
    );
}

// App identity colours for the menu icons (fixed, like the app tiles).
const iconTone = {
    docs: "text-[#185abd] dark:text-[#6ea0ea]",
    sheets: "text-[#107c41] dark:text-[#54c286]",
    slides: "text-[#c43e1c] dark:text-[#f08a6c]"
} as const;

/** "X of Y used" meter with a Manage link to the Storage page. Hidden until /api/quota answers. */
function StorageWidget({ active, onNavigate }: { active: boolean; onNavigate?: () => void }) {
    const q = useQuota();
    if (!q.data) return null;
    const used = q.data.usedBytes;
    const quota = q.data.quotaBytes;
    const pct = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
    const tone = pct >= 100 ? "bg-destructive" : pct >= 85 ? "bg-amber-500" : "bg-primary";
    return (
        <div className="mx-2.5 mb-1 pt-3 text-xs text-[var(--drive-text-2)]">
            <div className="flex items-center justify-between">
                <span>Storage</span>
                <Link
                    to="/storage"
                    onClick={onNavigate}
                    aria-current={active ? "page" : undefined}
                    className="rounded font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    Manage
                </Link>
            </div>
            <div
                className="my-1.5 h-1 w-full overflow-hidden rounded-sm bg-[var(--drive-subtle)]"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${pct}% of storage used`}
            >
                <div className={cn("h-full rounded-sm transition-all", tone)} style={{ width: `${pct}%` }} />
            </div>
            <span className="tabular-nums">
                {formatBytes(used)} of {formatBytes(quota)} used
            </span>
        </div>
    );
}
